/**
 * paste.js — rich text on the clipboard pastes as rvmark: blocks become nodes,
 * inline formatting becomes Markdown, headings take what follows as children.
 * Images and videos alone on a line become nodes; pasted image and video files
 * are saved beside the open file.
 */

import { ChangeSet } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { NODE_LINE, collectNodes, subtreeRange, headEnd, nextSiblingOrdinal } from 'rvmark/edit';

const BLOCK = new Set([
  'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DD', 'DETAILS', 'DIV', 'DL', 'DT',
  'FIELDSET', 'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'HEADER', 'HR', 'LI', 'MAIN', 'NAV', 'OL', 'P', 'PRE', 'SECTION', 'SUMMARY', 'TABLE', 'UL',
]);
const BLOCK_SELECTOR = [...BLOCK].join(',');
const SKIP = new Set(['HEAD', 'LINK', 'META', 'NOSCRIPT', 'SCRIPT', 'STYLE', 'TEMPLATE', 'TITLE']);
const CODE = new Set(['CODE', 'KBD', 'SAMP', 'TT']);
const MARK = { b: '**', i: '*', s: '~~' };
const NONE = { b: false, i: false, s: false };

const skipped = (el) => SKIP.has(el.tagName) || el.style?.display === 'none';
const longestRun = (s, re) => Math.max(0, ...[...s.matchAll(re)].map((m) => m[1].length));
const pct = (c) => '%' + c.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0');
const escapeCell = (s) => s.replace(/\n/g, ' ').replace(/\|/g, '\\|');
const absolute = (src) => (src?.startsWith('//') ? 'https:' + src : src?.trim());

// The URLs `{= video}` embeds, and media files by extension.
const VIDEO_PAGE = /^https?:\/\/(?:[\w-]+\.)*(?:youtube\.com\/.*[?&](?:v|list)=|youtu\.be\/|odysee\.com\/(?!\$\/)[^/?#]|vimeo\.com\/(?:\d|video\/\d|channels\/|groups\/))/i;
const VIDEO_FILE = /\.(?:mp4|webm|ogv|ogg|mov|m4v)(?:[?#]|$)/i;
const IMAGE_FILE = /\.(?:png|jpe?g|gif|webp|avif|svg|bmp|ico)(?:[?#]|$)/i;

function mediaAt(url) {
  if (!url || !/^https?:\/\/\S+$/i.test(url)) return null;
  const kind = VIDEO_PAGE.test(url) || VIDEO_FILE.test(url) ? 'video' : IMAGE_FILE.test(url) ? 'image' : null;
  return kind && { kind, src: url };
}

function flags(el, f) {
  const t = el.tagName;
  const { fontWeight: fw, fontStyle: fs } = el.style ?? {};
  const deco = el.style?.textDecorationLine || el.style?.textDecoration || '';
  return {
    b: fw ? fw === 'bold' || fw === 'bolder' || parseInt(fw, 10) >= 600 : f.b || t === 'B' || t === 'STRONG',
    i: fs ? fs !== 'normal' : f.i || t === 'I' || t === 'EM' || t === 'CITE' || t === 'DFN' || t === 'VAR',
    s: f.s || t === 'S' || t === 'DEL' || t === 'STRIKE' || deco.includes('line-through'),
  };
}

function escapeText(s) {
  return s
    .replace(/[\\`*[\]<>$~{}]/g, '\\$&')
    .replace(/&(?=#?\w+;)/g, '\\&')
    .replace(/_/g, (m, i) => (/[\p{L}\p{N}]/u.test(s[i - 1] ?? '') && /[\p{L}\p{N}]/u.test(s[i + 1] ?? '') ? m : '\\_'));
}

function codeSpan(text) {
  const t = text.replace(/\s+/g, ' ');
  if (!t.trim()) return null;
  const ticks = '`'.repeat(longestRun(t, /(`+)/g) + 1);
  const pad = /^`|`$/.test(t) || (t.startsWith(' ') && t.endsWith(' ')) ? ' ' : '';
  return ticks + pad + t + pad + ticks;
}

// KaTeX and MathML keep their TeX in an annotation; Wikipedia's in `alttext`.
function tex(el) {
  const isMath = el.tagName.toLowerCase() === 'math';
  if (!isMath && !el.classList.contains('katex') && !el.classList.contains('mwe-math-element')) return null;
  const math = isMath ? el : el.querySelector('math');
  const src = el.querySelector('annotation[encoding="application/x-tex"]')?.textContent
    ?? math?.getAttribute('alttext');
  if (!src?.trim()) return { text: el.querySelector('.katex-html')?.textContent ?? el.textContent };
  const display = el.parentElement?.classList.contains('katex-display') || math?.getAttribute('display') === 'block';
  return { atom: display ? `$$${src.trim()}$$` : `$${src.trim()}$` };
}

function linkDest(href) {
  return /[\s()<>]/.test(href) ? href.replace(/[\s()<>]/g, pct) : href;
}

function inlineEl(el, f, runs) {
  if (skipped(el)) return;
  const t = el.tagName;
  if (t === 'BR') { runs.push({ text: '\n' }); return; }
  const math = tex(el);
  if (math) { runs.push({ ...math, ...flags(el, f) }); return; }
  if (t === 'IMG') {
    const src = absolute(el.getAttribute('src'));
    const alt = el.getAttribute('alt')?.trim() ?? '';
    if (/^https?:/i.test(src)) {
      runs.push({ atom: `![${escapeText(alt)}](${linkDest(src)})`, media: { kind: 'image', src, alt }, ...f });
    }
    return;
  }
  if (t === 'VIDEO') {
    const src = absolute(el.getAttribute('src') ?? el.querySelector('source')?.getAttribute('src'));
    if (/^https?:/i.test(src)) runs.push({ atom: `<${linkDest(src)}>`, media: { kind: 'video', src }, ...f });
    return;
  }
  if (t === 'IFRAME') {
    const id = absolute(el.getAttribute('src'))?.match(/^https?:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/([\w-]{11})/)?.[1];
    if (id) {
      const src = `https://www.youtube.com/watch?v=${id}`;
      runs.push({ atom: `<${src}>`, media: { kind: 'video', src }, ...f });
    }
    return;
  }
  const g = flags(el, f);
  if (CODE.has(t)) {
    const atom = codeSpan(el.textContent);
    if (atom) runs.push({ atom, ...g });
    return;
  }
  // Only absolute links: a relative one would resolve against this site.
  const href = t === 'A' ? el.getAttribute('href')?.trim() : null;
  if (href && /^[a-z][a-z0-9+.-]*:/i.test(href) && !/^(javascript|data):/i.test(href)) {
    const label = el.textContent.trim();
    const innerRuns = collect(el.childNodes, NONE);
    // A linked image is the image: the link would leave it inline.
    const lone = alone(innerRuns);
    if (lone?.media) { runs.push({ ...lone, ...g }); return; }
    if (!label || label === href) {
      const media = mediaAt(href);
      if (media) { runs.push({ atom: `<${linkDest(href)}>`, media, ...g }); return; }
    }
    const inner = emit(innerRuns);
    if (!inner) return;
    const atom = label === href && /^https?:/i.test(href) ? `<${linkDest(href)}>` : `[${inner}](${linkDest(href)})`;
    runs.push({ atom, ...g });
    return;
  }
  if (BLOCK.has(t)) runs.push({ text: ' ' });
  collect(el.childNodes, g, runs);
  if (BLOCK.has(t)) runs.push({ text: ' ' });
}

/** The one run carrying anything, if there is just one. */
function alone(runs) {
  const solid = runs.filter((r) => r.atom !== undefined || r.text.trim());
  return solid.length === 1 ? solid[0] : null;
}

function collect(nodes, f, runs = []) {
  for (const n of nodes) {
    if (n.nodeType === Node.TEXT_NODE) runs.push({ text: n.data.replace(/[\s ]+/g, ' '), ...f });
    else if (n.nodeType === Node.ELEMENT_NODE) inlineEl(n, f, runs);
  }
  return runs;
}

// Markers close before whitespace and open after it, as CommonMark's flanking
// rules want; whitespace itself never opens or closes one.
function emit(runs) {
  let out = '';
  let pend = '';
  const open = [];
  const closeTo = (keep) => {
    let s = '';
    while (open.length > keep) s += MARK[open.pop()];
    return s;
  };
  for (const r of runs) {
    const tokens = r.atom !== undefined ? [r.atom] : r.text.match(/\s+|\S+/g) ?? [];
    for (const tok of tokens) {
      if (r.atom === undefined && !tok.trim()) { pend += tok; continue; }
      const want = Object.keys(MARK).filter((k) => r[k]);
      let keep = 0;
      while (keep < open.length && want.includes(open[keep])) keep++;
      const closers = closeTo(keep);
      const ws = out && pend ? (pend.includes('\n') ? '\n' : ' ') : '';
      let openers = '';
      for (const k of want) if (!open.includes(k)) { open.push(k); openers += MARK[k]; }
      out += closers + ws + openers + (r.atom ?? escapeText(tok));
      pend = '';
    }
  }
  return out + closeTo(0);
}

function preBody(pre) {
  const code = pre.textContent.replace(/\n$/, '');
  const lang = (pre.querySelector('code')?.className || pre.className).match(/language-([\w+-]+)/)?.[1] ?? '';
  const fence = '`'.repeat(Math.max(3, longestRun(code, /^\s*(`+)/gm) + 1));
  return `${fence}${lang}\n${code}\n${fence}`;
}

const item = (label, extra = {}) => ({ label, children: [], body: null, kind: null, ...extra });

// The first row heads the table; the rest are its `{= tr}` children.
function table(el) {
  const rows = [...el.rows].map((r) => [...r.cells].map((c) => escapeCell(emit(collect(c.childNodes, NONE)))));
  if (!rows.length) return null;
  const n = Math.max(...rows.map((r) => r.length));
  const cells = (r) => Array.from({ length: n }, (_, j) => r[j] ?? '').join(' | ');
  return item(cells(rows[0]), { kind: 'table', children: rows.slice(1).map((r) => item(cells(r), { kind: 'tr' })) });
}

function nest(items) {
  const root = [];
  const stack = [];
  for (const it of items) {
    if (it.heading) while (stack.length && stack.at(-1).heading >= it.heading) stack.pop();
    (stack.length ? stack.at(-1).children : root).push(it);
    if (it.heading) stack.push(it);
  }
  return root;
}

function listItem(li, f) {
  const inside = [];
  blocks(li, f, inside);
  if (!inside.length) return null;
  const lift = inside[0].body === null && !inside[0].kind;
  return item(lift ? inside[0].label : '', { children: nest(lift ? inside.slice(1) : inside) });
}

function list(el, f, out) {
  for (const c of el.children) {
    if (skipped(c)) continue;
    if (c.tagName === 'UL' || c.tagName === 'OL') list(c, f, out.at(-1)?.children ?? out);
    else if (c.tagName === 'LI') { const it = listItem(c, f); if (it) out.push(it); }
    else blocks(c, f, out);
  }
}

function blocks(parent, f, out) {
  let runs = [];
  const flush = () => {
    const lone = alone(runs);
    const media = lone && (lone.media ?? (lone.atom === undefined ? mediaAt(lone.text.trim()) : null));
    if (media) { out.push(item('', media)); runs = []; return; }
    const label = emit(runs).replace(/\n+/g, '\n').replace(/^\n|\n$/g, '');
    if (label.trim()) out.push(item(label));
    runs = [];
  };
  for (const n of parent.childNodes) {
    if (n.nodeType === Node.TEXT_NODE) { collect([n], f, runs); continue; }
    if (n.nodeType !== Node.ELEMENT_NODE || skipped(n)) continue;
    const t = n.tagName;
    if (!BLOCK.has(t)) {
      if (tex(n) || t === 'VIDEO' || !n.querySelector(BLOCK_SELECTOR)) { inlineEl(n, f, runs); continue; }
      flush();
      blocks(n, flags(n, f), out);
      continue;
    }
    flush();
    if (/^H[1-6]$/.test(t)) {
      const label = emit(collect(n.childNodes, flags(n, f))).replace(/\n+/g, ' ');
      if (label.trim()) out.push(item(label, { heading: +t[1] }));
    } else if (t === 'UL' || t === 'OL') list(n, f, out);
    else if (t === 'LI') { const it = listItem(n, f); if (it) out.push(it); }
    else if (t === 'PRE') out.push(item('', { body: preBody(n) }));
    else if (t === 'TABLE' && !n.querySelector('table')) {
      const it = table(n);
      if (it) out.push(it);
    } else if (t !== 'HR') blocks(n, flags(n, f), out);
  }
  flush();
}

/** The clipboard's HTML as a tree of items: `{ label, children, body }`. */
export function htmlToItems(html) {
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  const all = body.textContent.trim();
  // Code copied from a code view: the text is the point, not the markup.
  const preformatted = [...body.querySelectorAll('pre, [style*="white-space"]')]
    .some((el) => (el.tagName === 'PRE' || el.style.whiteSpace.startsWith('pre')) && el.textContent.trim() === all);
  if ((!all && !body.querySelector('img, video, iframe')) || preformatted) return null;
  const out = [];
  blocks(body, NONE, out);
  return nest(out);
}

// A continuation line that would read as a node line of its own.
const guard = (l) => (NODE_LINE.test(l) ? l.replace(/^([a-zA-Z0-9]+)\./, '$1\\.').replace(/^-/, '\\-') : l);

function renderItem(it, indent, marker, unit, out) {
  const head = `${indent}${marker} `;
  if (it.kind === 'image' || it.kind === 'video') {
    const alt = it.alt && it.alt.replace(/[{}]/g, '').replace(/["\\]/g, '\\$&');
    out.push(`${head}{= ${it.kind}${alt ? `; alt: "${alt}"` : ''}} ${linkDest(it.src)}`);
  } else if (it.kind === 'table' || it.kind === 'tr') {
    out.push(`${head}{= ${it.kind}} ${it.label}`.trimEnd());
  } else if (it.body !== null) {
    const inner = indent + unit;
    const fence = '~'.repeat(Math.max(3, longestRun(it.body, /^\s*(~+)/gm) + 1));
    out.push(head + '{= block}', inner + fence, ...it.body.split('\n').map((l) => (l ? inner + l : '')), inner + fence);
  } else {
    const cont = indent + ' '.repeat(marker.length + 1);
    const lines = it.label.split('\n');
    lines.forEach((l, j) => out.push((j ? cont + guard(l) : head + l) + (j < lines.length - 1 ? '\\' : '')));
  }
  for (const c of it.children) renderItem(c, indent + unit, '-', unit, out);
}

function indentUnit(nodes) {
  for (let i = 1; i < nodes.length; i++) {
    const a = nodes[i - 1].indent;
    const b = nodes[i].indent;
    if (b.length > a.length && b.startsWith(a)) return b.slice(a.length);
  }
  return '  ';
}

// Where the items go, against `state` (the document with the selection
// already deleted): inline at `pos` for a single plain paragraph, otherwise as
// nodes — filling a blank line or an empty bullet, or after the subtree of the
// node the cursor is in.
function placement(state, pos, items) {
  const doc = state.doc;
  const lines = doc.toString().split('\n');
  const line = doc.lineAt(pos);
  const li = line.number - 1;
  const h = headEnd(lines);
  if (li < h && lines.slice(li, h).some((l) => l.trim())) return null;
  const nodes = collectNodes(lines);
  if (nodes.some((n) => n.body && li >= n.body[0] && li <= n.body[1])) return null;

  let oi = -1;
  for (let i = 0; i < nodes.length && nodes[i].lineIndex <= li; i++) oi = i;
  const owner = oi === -1 ? null : nodes[oi];
  const onNode = owner?.lineIndex === li;
  const blank = !line.text.trim();

  const single = items.length === 1 && !items[0].children.length && items[0].body === null && !items[0].kind;
  if (single && !blank) {
    const cont = onNode
      ? owner.indent + ' '.repeat(owner.matchLen - owner.indent.length)
      : /^\s*/.exec(line.text)[0];
    const [first, ...rest] = items[0].label.split('\n');
    return { from: pos, to: pos, insert: [first, ...rest.map(guard)].join('\\\n' + cont) };
  }

  const unit = indentUnit(nodes);
  const render = (indent, markers) => {
    const out = [];
    items.forEach((it, k) => renderItem(it, indent, markers(k), unit, out));
    return out.join('\n');
  };
  if (blank) return { from: line.from, to: line.to, insert: render(line.text, () => '-') };
  if (!owner) return { from: line.to, to: line.to, insert: '\n' + render('', () => '-') };

  const marker = owner.ordinal;
  const next = /^[1-9][0-9]*$/.test(marker) ? nextSiblingOrdinal(lines, owner.lineIndex) : null;
  const sibling = (k) => (next !== null ? `${next + k}.` : marker === '*' ? '*' : '-');
  if (onNode && line.text.length === owner.matchLen) {
    const first = /^[*-]$/.test(marker) ? marker : marker + '.';
    return { from: line.from, to: line.to, insert: render(owner.indent, (k) => (k ? sibling(k - 1) : first)) };
  }
  const end = doc.line(subtreeRange(nodes, oi, lines)[1] + 1);
  return { from: end.to, to: end.to, insert: '\n' + render(owner.indent, sibling) };
}

/** Plain text that is nothing but media URLs, one to a line. */
function urlItems(text) {
  const media = text.split('\n').map((l) => l.trim()).filter(Boolean).map(mediaAt);
  return media.length && media.every(Boolean) ? media.map((m) => item('', m)) : null;
}

/**
 * `save(file)` names a pasted file beside the open document: `{ src, write }`,
 * the reference to embed it by and the call that stores it, or null.
 */
function clipboardItems(data, save) {
  const html = data.getData('text/html');
  // Office apps put a picture of the copied text beside its HTML; a file only
  // stands for the paste when the HTML carries no text of its own.
  const files = [...data.files].filter((f) => /^(image|video)\//.test(f.type));
  if (files.length && !new DOMParser().parseFromString(html, 'text/html').body.textContent.trim()) {
    return files.map((f) => {
      const saved = save(f);
      return saved && item('', { kind: f.type.startsWith('video/') ? 'video' : 'image', ...saved });
    }).filter(Boolean);
  }
  return html ? htmlToItems(html) : urlItems(data.getData('text/plain'));
}

export const richPaste = (save) => EditorView.domEventHandlers({
  paste(event, view) {
    const data = event.clipboardData;
    const { state } = view;
    if (!data || state.readOnly || state.selection.ranges.length > 1) return false;
    // VS Code's HTML is highlighted source.
    if (data.types.includes('vscode-editor-data')) return false;
    const items = clipboardItems(data, save);
    if (!items?.length) return false;

    const sel = state.selection.main;
    const del = sel.empty ? ChangeSet.empty(state.doc.length) : state.changes({ from: sel.from, to: sel.to });
    const cleared = sel.empty ? state : state.update({ changes: del }).state;
    const at = placement(cleared, sel.from, items);
    if (!at) return false;
    for (const it of items) it.write?.();

    event.preventDefault();
    view.dispatch({
      changes: del.compose(cleared.changes(at)),
      selection: { anchor: at.from + at.insert.length },
      scrollIntoView: true,
      userEvent: 'input.paste',
    });
    return true;
  },
});
